import React, { useState, useEffect, useCallback } from 'react';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  PhotoIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  ClockIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import AttributeLabel from '../components/AttributeLabel';
import apiClient from '../utils/apiClient';

interface ConfigData {
  general: {
    club_name: string;
    club_logo: string;
    street: string;
    house_number: string;
    postal_code: string;
    city: string;
    country: string;
    registration_number: string;
    court: string;
    full_address: string;
  };
}

interface MembershipContribution {
  year: number;
  adults: number;
  partner: number;
  youth: number;
}

interface CancellationPeriods {
  members: string | number;
  guests: number;
}


interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onClose?: () => void;
  buttons?: {
    cancel: { text: string; onPress: () => void };
    confirm: { text: string; onPress: () => void };
  };
  button?: { text: string; onPress: () => void };
}

const AdminConfig: React.FC = () => {
  const [editMode, setEditMode] = useState<string | null>(null);
  const [formData, setFormData] = useState<ConfigData>({
    general: {
      club_name: '',
      club_logo: '',
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      country: '',
      registration_number: '',
      court: '',
      full_address: ''
    }
  });
  const [originalData, setOriginalData] = useState<ConfigData>({
    general: {
      club_name: '',
      club_logo: '',
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      country: '',
      registration_number: '',
      court: '',
      full_address: ''
    }
  });
  const [changedFields, setChangedFields] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState<{ [key: string]: boolean }>({});
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  // Membership Management States
  const [contributions, setContributions] = useState<MembershipContribution[]>([]);
  const [cancellationPeriods, setCancellationPeriods] = useState<CancellationPeriods>({ 
    members: "unbegrenzt", 
    guests: 0 
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newYearData, setNewYearData] = useState({
    year: "",
    adults: "",
    partner: "",
    youth: "",
  });
  const [editingYear, setEditingYear] = useState<number | null>(null);
  const [editYearData, setEditYearData] = useState({
    year: "",
    adults: "",
    partner: "",
    youth: "",
  });
  const [editingCancellation, setEditingCancellation] = useState<string | null>(null);
  const [editCancellationData, setEditCancellationData] = useState({
    members: "",
    guests: "",
  });

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  useEffect(() => {
    fetchConfigData();
    loadLogo();
    fetchContributions();
    fetchCancellationPeriods();
  }, []);

  const loadLogo = async () => {
    try {
      if (logoSrc && logoSrc.startsWith('blob:')) {
        window.URL.revokeObjectURL(logoSrc);
      }
      
      const response = await apiClient.get('/api/config/logo', {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      setLogoSrc(url);
    } catch (error) {
      console.error('Error loading logo:', error);
      setLogoSrc('/default_logo.png');
    }
  };

  useEffect(() => {
    return () => {
      if (logoSrc && logoSrc.startsWith('blob:')) {
        window.URL.revokeObjectURL(logoSrc);
      }
    };
  }, [logoSrc]);

  const fetchConfigData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/config');
      const { data } = response.data;
      
      const configData: ConfigData = {
        general: {
          club_name: data.club_name || '',
          club_logo: data.club_logo || '',
          street: data.street || '',
          house_number: data.house_number || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          country: data.country || '',
          registration_number: data.registration_number || '',
          court: data.court || '',
          full_address: [data.street, data.house_number, data.postal_code, data.city, data.country]
            .filter(Boolean)
            .join(', '),
        },
      };
      setFormData(configData);
      setOriginalData(configData);
      
      if (data.club_logo) {
        loadLogo();
      } else {
        setLogoSrc('/default_logo.png');
      }
    } catch (err) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Laden der Daten.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContributions = async () => {
    try {
      const response = await apiClient.get('/api/config/membership_contributions');
      if (response.data.success) {
        setContributions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching contributions:', err);
    }
  };

  const fetchCancellationPeriods = async () => {
    try {
      const response = await apiClient.get('/api/config/cancellation_periods');
      if (response.data.success) {
        setCancellationPeriods(response.data.data || { members: "unbegrenzt", guests: 0 });
      }
    } catch (err) {
      console.error('Error fetching cancellation periods:', err);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);

    const uploadData = new FormData();
    uploadData.append('logo', file);

    try {
      const response = await apiClient.post('/api/config/upload_logo', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      setFormData((prev) => ({
        ...prev,
        general: { ...prev.general, club_logo: data.data.s3_key }
      }));
      loadLogo();
      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Logo erfolgreich hochgeladen!',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Hochladen.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoError = () => {
    setLogoSrc('/default_logo.png');
  };

  const handleInputChange = useCallback((section: string, name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section as keyof ConfigData], [name]: value },
    }));
    setChangedFields((prev: any) => ({
      ...prev,
      [section]: { ...prev[section], [name]: true },
    }));
  }, []);

  const getChangedData = (section: string) => {
    const changed: any = {};
    const sectionData = formData[section as keyof ConfigData];
    for (const key in sectionData) {
      if (changedFields[section]?.[key] && sectionData[key as keyof typeof sectionData] !== originalData[section as keyof ConfigData][key as keyof typeof sectionData]) {
        changed[key] = sectionData[key as keyof typeof sectionData];
      }
    }
    return changed;
  };

  const validateSection = (section: string) => {
    const errors: string[] = [];
    const data = formData[section as keyof ConfigData];

    if (section === 'general') {
      if (!data.club_name?.trim()) errors.push('Vereinsname ist erforderlich.');
    }
    
    return errors;
  };

  const handleSubmit = async (section: string, e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateSection(section);
    if (errors.length > 0) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: errors.join('\n'),
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
      return;
    }
    
    setSectionLoading((prev) => ({ ...prev, [section]: true }));
    
    try {
      const changedData = getChangedData(section);
      if (Object.keys(changedData).length === 0) {
        setConfirmModal({
          visible: true,
          type: 'info',
          title: 'Information',
          message: 'Keine Änderungen zum Speichern.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
        setEditMode(null);
        setSectionLoading((prev) => ({ ...prev, [section]: false }));
        return;
      }

      const response = await apiClient.patch('/api/config', { [section]: changedData });
      const data = response.data;
      
      if (data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: `${section.charAt(0).toUpperCase() + section.slice(1)} erfolgreich aktualisiert!`,
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
        setOriginalData((prev) => ({ 
          ...prev, 
          [section]: { ...prev[section as keyof ConfigData], ...changedData } 
        }));
        setChangedFields((prev: any) => ({ ...prev, [section]: {} }));
        setEditMode(null);
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: data.message || `Fehler beim Speichern der Gruppe ${section}.`,
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      }
    } catch (err) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: `Netzwerkfehler beim Speichern.`,
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setSectionLoading((prev) => ({ ...prev, [section]: false }));
    }
  };

  const handleCancel = (section: string) => {
    setFormData((prev) => ({ 
      ...prev, 
      [section]: { ...originalData[section as keyof ConfigData] } 
    }));
    setChangedFields((prev: any) => ({ ...prev, [section]: {} }));
    setEditMode(null);
  };

  // Membership Management Functions
  const handleAddYear = async () => {
    if (!newYearData.year || !newYearData.adults || !newYearData.partner || !newYearData.youth) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Bitte füllen Sie alle Felder aus.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
      return;
    }

    try {
      const response = await apiClient.post('/api/config/membership_contributions', {
        year: parseInt(newYearData.year),
        adults: parseFloat(newYearData.adults),
        partner: parseFloat(newYearData.partner),
        youth: parseFloat(newYearData.youth),
      });
      
      if (response.data.success) {
        await fetchContributions();
        setNewYearData({ year: "", adults: "", partner: "", youth: "" });
        setShowAddForm(false);
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Jahr erfolgreich hinzugefügt.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Fehler beim Hinzufügen des Jahres.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      }
    } catch (err: any) {
      // Zeige die spezifische Fehlermeldung von der API
      const errorMessage = err.response?.data?.message || err.message || 'Fehler beim Hinzufügen des Jahres.';
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: errorMessage,
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    }
  };

  const handleEditYear = (entry: MembershipContribution) => {
    setEditingYear(entry.year);
    setEditYearData({
      year: entry.year.toString(),
      adults: entry.adults.toString(),
      partner: entry.partner.toString(),
      youth: entry.youth.toString(),
    });
  };

  const handleSaveEditYear = async () => {
    try {
      const response = await apiClient.put(`/api/config/membership_contributions/${editingYear}`, {
        adults: parseFloat(editYearData.adults),
        partner: parseFloat(editYearData.partner),
        youth: parseFloat(editYearData.youth),
      });
      
      if (response.data.success) {
        await fetchContributions();
        setEditingYear(null);
        setEditYearData({ year: "", adults: "", partner: "", youth: "" });
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Jahr erfolgreich aktualisiert.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Fehler beim Aktualisieren des Jahres.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      }
    } catch (err) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Aktualisieren des Jahres.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    }
  };

  const handleDeleteYear = async (year: number) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Löschen bestätigen',
      message: `Möchten Sie das Jahr ${year} wirklich löschen?`,
      variant: 'two',
      buttons: {
        cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) },
        confirm: { 
          text: 'Löschen', 
          onPress: async () => {
            try {
              const response = await apiClient.delete(`/api/config/membership_contributions/${year}`);
              if (response.data.success) {
                await fetchContributions();
                setConfirmModal({
                  visible: true,
                  type: 'success',
                  title: 'Erfolgreich',
                  message: 'Jahr erfolgreich gelöscht.',
                  variant: 'single',
                  button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
                });
              } else {
                setConfirmModal({
                  visible: true,
                  type: 'error',
                  title: 'Fehler',
                  message: response.data.message || 'Fehler beim Löschen des Jahres.',
                  variant: 'single',
                  button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
                });
              }
            } catch (err) {
              setConfirmModal({
                visible: true,
                type: 'error',
                title: 'Fehler',
                message: 'Fehler beim Löschen des Jahres.',
                variant: 'single',
                button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
              });
            }
          }
        }
      }
    });
  };

  const handleEditCancellation = (type: string) => {
    setEditingCancellation(type);
    setEditCancellationData({
      members: cancellationPeriods.members === "unbegrenzt" ? "" : cancellationPeriods.members.toString(),
      guests: cancellationPeriods.guests.toString(),
    });
  };

  const handleSaveCancellation = async () => {
    try {
      const response = await apiClient.put('/api/config/cancellation_periods', {
        members: editCancellationData.members === "" ? "unbegrenzt" : parseInt(editCancellationData.members),
        guests: parseInt(editCancellationData.guests),
      });
      
      if (response.data.success) {
        await fetchCancellationPeriods();
        setEditingCancellation(null);
        setEditCancellationData({ members: "", guests: "" });
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Stornierungsfristen erfolgreich aktualisiert.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Fehler beim Aktualisieren der Stornierungsfristen.',
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
        });
      }
    } catch (err) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Aktualisieren der Stornierungsfristen.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-3">
              <div className="h-5 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const renderSection = (
    section: string, 
    title: string, 
    icon: React.ReactNode, 
    fields: Array<{name: string; label: string; type?: string; required?: boolean; placeholder?: string; iconKey?: React.ComponentProps<typeof AttributeLabel>['iconKey']}>,
    isCheckboxSection = false
  ) => (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
      <div className="mb-5 flex w-full min-w-0 flex-col items-start justify-between gap-3 border-b border-gray-100 pb-4 dark:border-gray-700 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <h2 className="flex min-w-0 items-center gap-2.5 text-lg font-semibold text-gray-900 dark:text-white">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">{icon}</span>
            {title}
          </h2>
          {section === 'general' && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:ml-10">Stammdaten und rechtliche Angaben des Vereins</p>
          )}
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          {editMode !== section ? (
            <button
              onClick={() => setEditMode(section)}
              disabled={sectionLoading[section]}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-300 sm:w-auto"
            >
              <PencilIcon className="h-4 w-4" /> Bearbeiten
            </button>
          ) : (
            <>
              <button
                onClick={(e) => handleSubmit(section, e)}
                disabled={sectionLoading[section]}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {sectionLoading[section] ? (
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z" />
                  </svg>
                ) : (
                  <CheckIcon className="h-4 w-4" />
                )}
                Speichern
              </button>
              <button
                onClick={() => handleCancel(section)}
                disabled={sectionLoading[section]}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 sm:w-auto"
              >
                <XMarkIcon className="h-4 w-4" /> Abbrechen
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-x-3 gap-y-4 md:grid-cols-2 md:gap-x-5">
        {!isCheckboxSection && fields.map(({ name, label, type = 'text', required, placeholder, iconKey }) => (
          <div
            key={`${section}-${name}`}
            className={`min-w-0 ${['club_name', 'club_logo', 'registration_number', 'court', 'country'].includes(name) ? 'col-span-2 md:col-span-1' : ''}`}
          >
            {iconKey ? (
              <AttributeLabel
                iconKey={iconKey}
                htmlFor={`${section}-${name}`}
                className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {label}
              </AttributeLabel>
            ) : (
              <label htmlFor={`${section}-${name}`} className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {label}
              </label>
            )}
            {type === 'logo' ? (
              <div className="flex min-h-16 items-center gap-4 rounded-xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-700 dark:bg-gray-800/60">
                <img
                  src={logoSrc || '/default_logo.png'}
                  alt="Vereinslogo"
                  className="h-14 w-14 rounded-lg bg-white object-contain p-1 dark:bg-gray-900"
                  onError={handleLogoError}
                />
                {editMode === section && (
                  <label
                    className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-700 dark:hover:text-primary-300"
                    aria-label="Logo hochladen"
                  >
                    <PhotoIcon className="h-4 w-4" />
                    Logo ändern
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleLogoUpload}
                      disabled={sectionLoading[section]}
                    />
                  </label>
                )}
              </div>
            ) : editMode === section ? (
              <input
                id={`${section}-${name}`}
                type={type}
                name={name}
                value={formData[section as keyof ConfigData][name as keyof typeof formData.general] || ''}
                onChange={(e) => handleInputChange(section, name, e.target.value)}
                disabled={sectionLoading[section]}
                required={required}
                className={`h-11 w-full rounded-xl border px-3 text-sm text-gray-900 outline-none transition-colors dark:text-gray-100 ${
                  changedFields[section]?.[name]
                    ? 'border-warning-500 bg-warning-50 dark:bg-warning-900/20'
                    : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                } focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20`}
                placeholder={placeholder}
                aria-label={label}
              />
            ) : (
              <div className="flex min-h-11 min-w-0 items-center break-words rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2 text-sm font-medium text-gray-900 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-100">
                {formData[section as keyof ConfigData][name as keyof typeof formData.general] || <span className="font-normal text-gray-400 dark:text-gray-500">Nicht angegeben</span>}
              </div>
            )}
            {editMode === section && required && !formData[section as keyof ConfigData][name as keyof typeof formData.general]?.trim() && (
              <p className="text-error-500 dark:text-error-400 text-xs mt-1">Pflichtfeld.</p>
            )}
          </div>
        ))}
        {section === 'general' && (
          <div className="md:col-span-2">
            <AttributeLabel iconKey="address" className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Vollständige Adresse
            </AttributeLabel>
            <div className="flex min-h-11 min-w-0 items-center break-words rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
              {formData[section].full_address || 'Wird automatisch aus den Adressfeldern gebildet'}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="mx-auto w-full max-w-7xl">
      {/* Header - Variante 1: Einfacher Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Cog6ToothIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Vereinseinstellungen</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Konfiguration der Vereinsdaten und Systemeinstellungen</p>
            </div>
          </div>
        </div>
      </header>

      <div className="space-y-6">
        {renderSection('general', 'Allgemeine Einstellungen', <MapPinIcon className="h-5 w-5" />, [
          { name: 'club_name', label: 'Vereinsname', required: true, placeholder: 'Vereinsname', iconKey: 'organization' },
          { name: 'club_logo', label: 'Vereinslogo', type: 'logo', placeholder: '', iconKey: 'logo' },
          { name: 'registration_number', label: 'Vereins Registrierungsnummer', required: false, placeholder: 'VR 12345', iconKey: 'registrationNumber' },
          { name: 'court', label: 'Gericht', placeholder: 'Amtsgericht Musterstadt', iconKey: 'court' },
          { name: 'street', label: 'Straße', placeholder: 'Musterstraße', iconKey: 'street' },
          { name: 'house_number', label: 'Hausnummer', placeholder: '123', iconKey: 'houseNumber' },
          { name: 'postal_code', label: 'PLZ', placeholder: '12345', iconKey: 'address' },
          { name: 'city', label: 'Ort', placeholder: 'Musterstadt', iconKey: 'city' },
          { name: 'country', label: 'Land', placeholder: 'Deutschland', iconKey: 'country' },
        ])}

        {/* Mitgliedsbeiträge Section */}
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
          <div className="mb-5 flex flex-col items-start justify-between gap-3 border-b border-gray-100 pb-4 dark:border-gray-700 sm:flex-row sm:items-center">
            <div>
              <h2 className="flex items-center gap-2.5 text-lg font-semibold text-gray-900 dark:text-white">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400"><UserGroupIcon className="h-5 w-5" /></span>
                Mitgliedsbeiträge
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:ml-10">Jahresbeiträge nach Mitgliedsart</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 sm:w-auto"
            >
              <PlusIcon className="h-4 w-4" />
              {showAddForm ? "Abbrechen" : "Neues Jahr hinzufügen"}
            </button>
          </div>

          {showAddForm && (
            <div className="mb-4 rounded-xl border border-primary-200 bg-primary-50/50 p-4 dark:border-primary-800 dark:bg-primary-900/10">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div>
                  <AttributeLabel iconKey="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Jahr
                  </AttributeLabel>
                  <input
                    type="number"
                    placeholder="z.B. 2027"
                    value={newYearData.year}
                    onChange={(e) => setNewYearData({ ...newYearData, year: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Erwachsene
                  </AttributeLabel>
                  <input
                    type="number"
                    placeholder="€"
                    value={newYearData.adults}
                    onChange={(e) => setNewYearData({ ...newYearData, adults: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Partner
                  </AttributeLabel>
                  <input
                    type="number"
                    placeholder="€"
                    value={newYearData.partner}
                    onChange={(e) => setNewYearData({ ...newYearData, partner: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <AttributeLabel iconKey="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Jugendliche
                  </AttributeLabel>
                  <input
                    type="number"
                    placeholder="€"
                    value={newYearData.youth}
                    onChange={(e) => setNewYearData({ ...newYearData, youth: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <button
                onClick={handleAddYear}
                className="mt-4 h-10 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
              >
                Hinzufügen
              </button>
            </div>
          )}

          <div className="space-y-2 sm:hidden">
            {contributions.map((entry) => (
              <div key={entry.year} className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-700 dark:bg-gray-800/60">
                {editingYear === entry.year ? (
                  <>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="font-bold text-gray-900 dark:text-white">Beitrag {entry.year}</p>
                      <div className="flex gap-1.5">
                        <button onClick={handleSaveEditYear} className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white" aria-label={`${entry.year} speichern`}><CheckIcon className="h-4 w-4" /></button>
                        <button onClick={() => setEditingYear(null)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300" aria-label="Bearbeitung abbrechen"><XMarkIcon className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        ['adults', 'Erwachsene'],
                        ['partner', 'Partner'],
                        ['youth', 'Jugendliche'],
                      ] as const).map(([key, label]) => (
                        <label key={key} className="min-w-0">
                          <span className="mb-1 block truncate text-[9px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
                          <input type="number" value={editYearData[key]} onChange={(e) => setEditYearData({ ...editYearData, [key]: e.target.value })} className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 outline-none focus:border-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{entry.year}</p>
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEditYear(entry)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 dark:border-gray-600 dark:bg-gray-800 dark:text-primary-400" aria-label={`${entry.year} bearbeiten`}><PencilIcon className="h-4 w-4" /></button>
                        <button onClick={() => handleDeleteYear(entry.year)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-error-200 bg-error-50 text-error-600 dark:border-error-800 dark:bg-error-900/20 dark:text-error-400" aria-label={`${entry.year} löschen`}><TrashIcon className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700">
                      {([
                        ['Erwachsene', entry.adults],
                        ['Partner', entry.partner],
                        ['Jugendliche', entry.youth],
                      ] as const).map(([label, amount]) => (
                        <div key={label} className="min-w-0 px-2 first:pl-0 last:pr-0">
                          <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                          <p className="mt-1 text-sm font-bold tabular-nums text-gray-900 dark:text-white">{Number(amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-400">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Jahr</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Erwachsene</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Partner</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Jugendliche</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {contributions.map((entry, index) => (
                  <tr key={index} className="border-b border-gray-100 bg-white last:border-b-0 dark:border-gray-700 dark:bg-[#1c2432]">
                    {editingYear === entry.year ? (
                      <>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editYearData.year}
                            onChange={(e) => setEditYearData({ ...editYearData, year: e.target.value })}
                            className="px-2 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                            disabled
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editYearData.adults}
                            onChange={(e) => setEditYearData({ ...editYearData, adults: e.target.value })}
                            className="px-2 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editYearData.partner}
                            onChange={(e) => setEditYearData({ ...editYearData, partner: e.target.value })}
                            className="px-2 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editYearData.youth}
                            onChange={(e) => setEditYearData({ ...editYearData, youth: e.target.value })}
                            className="px-2 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                          />
                        </td>
                        <td className="flex justify-end gap-2 px-4 py-3">
                          <button
                            onClick={handleSaveEditYear}
                            className="text-success-600 dark:text-success-400 hover:text-success-700 dark:hover:text-success-300"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setEditingYear(null)}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100">{entry.year}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-200">{Number(entry.adults).toFixed(2)} €</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-200">{Number(entry.partner).toFixed(2)} €</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-200">{Number(entry.youth).toFixed(2)} €</td>
                        <td className="flex justify-end gap-2 px-4 py-3">
                          <button
                            onClick={() => handleEditYear(entry)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteYear(entry.year)}
                            className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Stornierungsfristen Section */}
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432] sm:p-6">
          <div className="mb-5 border-b border-gray-100 pb-4 dark:border-gray-700">
            <h2 className="flex items-center gap-2.5 text-lg font-semibold text-gray-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400"><ClockIcon className="h-5 w-5" /></span>
              Stornierungsfristen
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:ml-10">Zeitraum für kostenfreie Absagen vor einem Termin</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/60">
              <AttributeLabel iconKey="member" className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Mitglieder</AttributeLabel>
              {editingCancellation === 'members' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="unbegrenzt oder Anzahl Stunden"
                    value={editCancellationData.members}
                    onChange={(e) => setEditCancellationData({ ...editCancellationData, members: e.target.value })}
                    className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={handleSaveCancellation}
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-white"
                    aria-label="Stornierungsfrist speichern"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setEditingCancellation(null)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    aria-label="Bearbeitung abbrechen"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {cancellationPeriods.members === "unbegrenzt" ? "Unbegrenzt" : `${cancellationPeriods.members} Stunden`}
                  </p>
                  <button
                    onClick={() => handleEditCancellation('members')}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 transition-colors hover:border-primary-300 hover:bg-primary-50 dark:border-gray-600 dark:bg-gray-800 dark:text-primary-400 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
                    aria-label="Stornierungsfrist für Mitglieder bearbeiten"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/60">
              <AttributeLabel iconKey="guest" className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Gäste</AttributeLabel>
              {editingCancellation === 'guests' ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Anzahl Stunden"
                    value={editCancellationData.guests}
                    onChange={(e) => setEditCancellationData({ ...editCancellationData, guests: e.target.value })}
                    className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={handleSaveCancellation}
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-white"
                    aria-label="Stornierungsfrist speichern"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setEditingCancellation(null)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    aria-label="Bearbeitung abbrechen"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{cancellationPeriods.guests} Stunden</p>
                  <button
                    onClick={() => handleEditCancellation('guests')}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 transition-colors hover:border-primary-300 hover:bg-primary-50 dark:border-gray-600 dark:bg-gray-800 dark:text-primary-400 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
                    aria-label="Stornierungsfrist für Gäste bearbeiten"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={confirmModal.onClose}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.buttons}
        button={confirmModal.button}
      />
    </div>
  );
};

export default AdminConfig;
